import React, { useCallback, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core'
import { getAdminMediaFileErrors } from 'ServerAPI'
import Button from 'components/base/Button'
import Input from 'components/base/Input'
import Select from 'components/base/Select'
import MenuItem from 'components/base/MenuItem'
import { CircularProgress } from '~/components/base/CircularProgress/CircularProgress'
import { formatUnixTime } from '~/components/admin/formatUnixTime'
import { usePageSize } from '~/components/admin/usePageSize'
import { AppRoutes } from '~/AppRoutes'
import { ValuePopup, ValuePopupText } from '~/components/admin/ValuePopup'
import './MediaFileErrors.scss'

const searchByOptions = [
  'fileId',
  'userId',
  'fileType',
  'url',
  'codecsInfo',
  'errorType',
  'fileSignatureHex',
]

const hasValue = value => value !== null && value !== undefined && value !== ''

const getQueryState = locationSearch => {
  const queryParams = new URLSearchParams(locationSearch)
  const searchByFromQuery = queryParams.get('searchBy') || queryParams.get('sortBy')
  return {
    search: queryParams.get('search') || '',
    searchBy: searchByOptions.includes(searchByFromQuery) ? searchByFromQuery : 'fileId',
  }
}

const getSearchUrl = (searchBy, search) => {
  const queryParams = new URLSearchParams()
  queryParams.set('sortBy', searchBy)
  queryParams.set('search', String(search))
  return `${AppRoutes.AdminMediaFileErrors}?${queryParams.toString()}`
}

const getSearchValue = (value, searchBy, displayValue = value) => {
  if (!hasValue(value)) return value
  return (
    <Link
      className="admin-panel__link"
      to={getSearchUrl(searchBy, value)}
    >
      {String(displayValue)}
    </Link>
  )
}

const getUserIdValue = value => {
  if (!hasValue(value)) return value
  return (
    <Link
      className="admin-panel__link"
      to={`${AppRoutes.AdminRecentUsers}/${value}`}
    >
      {String(value)}
    </Link>
  )
}

const getUrlValue = value => {
  if (!hasValue(value)) return value
  return (
    <a
      className="admin-panel__link"
      href={value}
      target="_blank"
      rel="noopener noreferrer"
    >
      {String(value)}
    </a>
  )
}

export const MediaFileErrors = () => {
  const { pageSize, tableRef, isReady } = usePageSize()
  const location = useLocation()
  const initialQueryState = getQueryState(location.search)
  const [ searchText, setSearchText ] = useState(initialQueryState.search)
  const [ search, setSearch ] = useState(initialQueryState.search)
  const [ searchBy, setSearchBy ] = useState(initialQueryState.searchBy)
  const [ start, setStart ] = useState(0)
  const [ entries, setEntries ] = useState([])
  const [ totalCount, setTotalCount ] = useState(0)
  const [ signatureValue, setSignatureValue ] = useState(null)
  const [ loading, setLoading ] = useState(false)

  useEffect(() => {
    const nextQueryState = getQueryState(location.search)
    setStart(0)
    setSearchText(nextQueryState.search)
    setSearch(nextQueryState.search)
    setSearchBy(nextQueryState.searchBy)
  }, [ location.search ])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getAdminMediaFileErrors({
        start_number: start,
        count: pageSize,
        search,
        searchBy,
      })
      setEntries(data.entries || [])
      setTotalCount(data.totalCount || 0)
    } catch {
      setEntries([])
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }, [ start, search, searchBy, pageSize ])

  useEffect(() => {
    if (!isReady) return
    load()
  }, [ load, isReady ])

  const onSearch = () => {
    setStart(0)
    setSearch(searchText)
  }

  const onPage = diff => {
    const nextStart = start + diff * pageSize
    if (nextStart < 0 || nextStart >= totalCount) return
    setStart(nextStart)
  }

  const currentPage = totalCount ? Math.floor(start / pageSize) + 1 : 0
  const totalPages = Math.ceil(totalCount / pageSize)

  return (
    <div className="admin-list">
      <div className="admin-list__search">
        <span className="admin-list__search__label">Search by</span>
        <Select
          className="admin-list__search__select admin-list__search__select--errors"
          dropdownClassName="admin-list__search__dropdown--errors"
          value={searchBy}
          onChange={e => {
            setSearchBy(e.target.value)
            setStart(0)
          }}
        >
          {searchByOptions.map(option => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </Select>
        <Input
          id="admin-media-file-errors-search"
          className="admin-list__search__input"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onEnterKeyDown={onSearch}
        />
        <Button className="admin-list__search__button" primary onClick={onSearch}>
          Search
        </Button>
      </div>

      <div className="admin-list__table" ref={tableRef}>
        <Table
          className="admin-list__table__errors"
          aria-label="media file errors"
        >
          <TableHead>
            <TableRow>
              <TableCell className="admin-list__table__file-id">fileId</TableCell>
              <TableCell>userId</TableCell>
              <TableCell>fileType</TableCell>
              <TableCell className="admin-list__table__url">url</TableCell>
              <TableCell>codecsInfo</TableCell>
              <TableCell>errorType</TableCell>
              <TableCell>fileSignatureHex</TableCell>
              <TableCell>createTime</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(entry => (
              <TableRow key={entry.fileId}>
                <TableCell className="admin-list__table__file-id">
                  {getSearchValue(entry.fileId, 'fileId')}
                </TableCell>
                <TableCell>{getUserIdValue(entry.userId)}</TableCell>
                <TableCell>{getSearchValue(entry.fileType, 'fileType')}</TableCell>
                <TableCell className="admin-list__table__url">{getUrlValue(entry.url)}</TableCell>
                <TableCell>{getSearchValue(entry.codecsInfo, 'codecsInfo')}</TableCell>
                <TableCell>{getSearchValue(entry.errorType, 'errorType')}</TableCell>
                <TableCell className="admin-list__table__signature">
                  <ValuePopupText
                    value={entry.fileSignatureHex}
                    onClick={() => setSignatureValue(entry.fileSignatureHex)}
                  />
                </TableCell>
                <TableCell>{formatUnixTime(entry.createTime)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ValuePopup
        title="fileSignatureHex"
        value={signatureValue}
        open={Boolean(signatureValue)}
        onClose={() => setSignatureValue(null)}
      />

      <div className="admin-list__footer">
        <If condition={loading}>
          <CircularProgress endless percents={80} transparent size={24} />
        </If>
        <div className="admin-list__footer__nav">
          <Button
            className="admin-list__footer__nav__btn"
            secondary
            onClick={() => onPage(-1)}
            disabled={start === 0}
          >
            Prev
          </Button>
          <Button
            className="admin-list__footer__nav__btn"
            secondary
            onClick={() => onPage(1)}
            disabled={totalCount === 0 || start + pageSize >= totalCount}
          >
            Next
          </Button>
        </div>
        <span className="admin-list__footer__info">
          {'page '}
          {String(currentPage)}
          {' of '}
          {String(totalPages)}
          {', totalCount: '}
          {String(totalCount)}
        </span>
      </div>
    </div>
  )
}
